import React, { FC } from 'react'
import { graphql, usePaginationFragment } from 'react-relay'

import {
  Button,
  Column,
  Grid,
  HorizontalLine,
  Stack,
  Text,
  useBreakpoint,
  UserShortCard,
} from '@components'

import { INITIAL_COUNTS } from './GuildMembersScreen'

import { GuildMembersNonOrganizersSection_guild$key } from './__generated__/GuildMembersNonOrganizersSection_guild.graphql'

export const GuildMembersNonOrganizersSection: FC<{
  guild: GuildMembersNonOrganizersSection_guild$key
}> = ({ guild }) => {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment GuildMembersNonOrganizersSection_guild on Guild
      @refetchable(queryName: "GuildMembersNonOrganizersSectionPaginationQuery")
      @argumentDefinitions(
        count: { type: "Int", defaultValue: 12 }
        cursor: { type: "Cursor" }
      ) {
        members(first: $count, after: $cursor)
          @connection(key: "GuildMembersNonOrganizersSection_guild_members") {
          edges {
            node {
              ...UserShortCard_user
            }

            cursor
          }
        }
      }
    `,
    guild
  )

  const breakpoint = useBreakpoint()

  if (data.members.edges.length === 0) {
    return null
  }

  if (breakpoint === 0) {
    return (
      <Stack gap={4}>
        <Stack direction='horizontal' gap={4} alignItems='center'>
          <Text size={4} variant='cardHeading'>
            Members
          </Text>

          <HorizontalLine variant='cardHeading' />
        </Stack>

        {data.members.edges.map((edge) => (
          <UserShortCard key={edge.cursor} user={edge.node} />
        ))}

        {hasNext && (
          <Button
            variant='secondary'
            hoverVariant='secondaryHover'
            onPress={() => {
              if (isLoadingNext) {
                return
              }

              loadNext(12)
            }}
          >
            <Text variant='buttonSecondary' size={4}>
              {isLoadingNext ? 'Loading...' : 'More'}
            </Text>
          </Button>
        )}
      </Stack>
    )
  }

  const memberGroups = data.members.edges.reduce<
    Array<Array<(typeof data.members.edges)[number]>>
  >((memo, member, index) => {
    if (index % INITIAL_COUNTS[breakpoint] === 0) {
      memo.push([member])
    } else {
      memo[memo.length - 1].push(member)
    }

    return memo
  }, [])

  return (
    <Stack variant='paddedBottom' gap={4}>
      <Stack direction='horizontal' gap={4} alignItems='center'>
        <Text size={4} variant='cardHeading'>
          Members
        </Text>

        <HorizontalLine variant='cardHeading' />
      </Stack>

      <Stack gap={4}>
        {memberGroups.map((group, index) => {
          return (
            <Stack key={index}>
              <Grid gap={4}>
                {group.map((edge) => (
                  <Column key={edge.cursor}>
                    <UserShortCard user={edge.node} expand />
                  </Column>
                ))}

                {INITIAL_COUNTS[breakpoint] - group.length > 0 ? (
                  <Column span={INITIAL_COUNTS[breakpoint] - group.length} />
                ) : null}
              </Grid>
            </Stack>
          )
        })}
      </Stack>

      {hasNext ? (
        <Button
          variant='secondary'
          hoverVariant='secondaryHover'
          onPress={() => {
            if (isLoadingNext) {
              return
            }

            loadNext(12)
          }}
        >
          <Text variant='buttonSecondary' size={4}>
            {isLoadingNext ? 'Loading...' : 'More'}
          </Text>
        </Button>
      ) : null}
    </Stack>
  )
}
